import type { OperatorToken } from '@matter_extractor/shared';
import { ApiError } from './api';
import { versionedDay } from './versioned-labels';

/**
 * Every French sentence of Réglages > Jetons, and the two command blocks the
 * one-time panel hands over (§20.2, issue #84).
 *
 * Pure, and in a file of its own for the reason `lib/link-labels.ts` gives:
 * what a screen SAYS is the half of it a reviewer reads off a diff, and it is
 * the half a render test cannot assert twice over without repeating the
 * component. The components below it keep the shape; this keeps the words.
 */

/** The two states a token is ever in, as the list names them. */
export type TokenState = 'active' | 'revoked';

/**
 * A token has no `state` on the wire - it has a `revokedAt` (§20.2), and the
 * state is read off it. One reading, here, rather than a `revokedAt !== null`
 * spelled in the row, in the badge and in the button that must not appear.
 */
export function tokenState(token: OperatorToken): TokenState {
  return token.revokedAt === null ? 'active' : 'revoked';
}

/** The badge. A `Record` over the union: a third state stops compiling. */
export const TOKEN_STATE_LABEL: Readonly<Record<TokenState, string>> = {
  active: 'actif',
  revoked: 'révoqué',
};

/**
 * Dates are DAYS here, and that is the backend's own reading rather than a
 * shortcut: `last_used_at` answers « ce jeton dort-il ? », a question read on a
 * scale of days, and the column is only rewritten once a minute for that very
 * reason. `versionedDay` is the admin's date, hand-rolled so the month is
 * French on every machine that renders it - a capture script included.
 */
export function tokenMintSentence(token: OperatorToken): string {
  return `Frappé le ${versionedDay(token.createdAt)}`;
}

/**
 * « jamais » is a real answer and the most useful one on this screen: it names
 * the token that was minted, pasted nowhere, and forgotten - the one to
 * revoke first.
 */
export function tokenLastUseSentence(token: OperatorToken): string {
  if (token.lastUsedAt === null) return 'Dernier usage : jamais';
  return `Dernier usage : ${versionedDay(token.lastUsedAt)}`;
}

/** Only ever shown on a revoked row, where it is the last fact left to read. */
export function tokenRevokedSentence(token: OperatorToken): string {
  return token.revokedAt === null ? '' : `Révoqué le ${versionedDay(token.revokedAt)}`;
}

/**
 * « 3 jetons, dont 2 actifs » - the count beside the list's heading.
 *
 * Zero takes the SINGULAR - « dont 0 actif » - which is the French rule and not
 * a taste: only a count strictly greater than one carries the plural mark. The
 * case is reachable in one gesture, a list whose every token has been revoked.
 */
export function tokenCountSentence(tokens: readonly OperatorToken[]): string {
  if (tokens.length === 0) return 'aucun jeton';
  const active = tokens.filter((token) => token.revokedAt === null).length;
  const total = tokens.length === 1 ? '1 jeton' : `${tokens.length} jetons`;
  return `${total}, dont ${active > 1 ? `${active} actifs` : `${active} actif`}`;
}

/**
 * The two lines to paste, per shell.
 *
 * They exist because the token is shown ONCE: an operator who has to invent
 * the variable names from a paragraph gets one attempt at it, and the panel is
 * gone by the time they find out they mistyped `MATTER_PAT`. The names are the
 * ones the committed `.mcp.json` reads (README, « Assistant MCP »), so a paste
 * is the whole of the wiring.
 *
 * `setx` writes the variable for the NEXT shell and not the current one, which
 * is Windows and not something to hide: the sentence under the block says to
 * open a new terminal. The bash pair is an `export`, which is the session -
 * and a `.bashrc` line is a decision about a secret's lifetime that belongs to
 * the person, not to a screen.
 */
export function powershellCommands(token: string, apiUrl: string): string {
  return `setx MATTER_PAT "${token}"\nsetx MATTER_API_URL "${apiUrl}"`;
}

export function bashCommands(token: string, apiUrl: string): string {
  return `export MATTER_PAT="${token}"\nexport MATTER_API_URL="${apiUrl}"`;
}

/* --------------------------------------------------- what a write refused on */

/**
 * The sentences a failed gesture reaches for, read off the STATUS.
 *
 * The régime of `lib/model-labels.ts` and of `lib/admin-labels.ts`: a screen
 * words a refusal from what the server ANSWERED, never from its `message` -
 * which is an English developer string belonging to the logs. The one exception
 * is spelt out below and is deliberate.
 *
 * Three statuses are not interchangeable here, and reading all three as « une
 * erreur, réessayez » is what this replaces:
 *
 * - **401** is the cookie dying mid-afternoon. « Réessayez » sends an operator
 *   pressing a button that will refuse again, for as long as they keep
 *   pressing; the honest instruction is to sign in first.
 * - **400** is the only place the server sentence is shown, and only because
 *   the bound it names is the one the form failed to enforce (a label over
 *   `OPERATOR_TOKEN_LABEL_MAX`). It is a defect of ours, and hiding the reason
 *   would leave an operator retyping the same label.
 * - **404 on a revocation** is not a failure to repair: the token is gone, or
 *   it was never this address's. The list is re-read behind the sentence, so
 *   the row the operator was looking at disappears with it.
 */
export const TOKEN_FAILURE_WORDING = {
  expired: 'Votre session a expiré : reconnectez-vous, puis recommencez.',
  mint: 'Le jeton n’a pas pu être frappé. Réessayez.',
  revoke: 'Le jeton n’a pas pu être révoqué. Réessayez.',
  gone: 'Ce jeton n’existe plus, ou n’est pas le vôtre.',
} as const;

/** The status of a failure, or `null` when it did not come from the wire. */
function statusOf(error: unknown): number | null {
  return error instanceof ApiError ? error.status : null;
}

/** Why the strike failed, or `null` when it did not. */
export function mintFailureSentence(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  const status = statusOf(error);
  if (status === 401) return TOKEN_FAILURE_WORDING.expired;
  if (status === 400 && error instanceof ApiError) return error.message;
  return TOKEN_FAILURE_WORDING.mint;
}

/** Why the revocation failed, or `null` when it did not. */
export function revokeFailureSentence(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  const status = statusOf(error);
  if (status === 401) return TOKEN_FAILURE_WORDING.expired;
  if (status === 404) return TOKEN_FAILURE_WORDING.gone;
  if (status === 400 && error instanceof ApiError) return error.message;
  return TOKEN_FAILURE_WORDING.revoke;
}
